import { alpha, iconClasses } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { CSSObject } from "tss-react"
import { Size } from "./types"

type StyleParams = {
  size: Size
}

// The lg font pair predates the type scale and is kept literal so the study plan stays unchanged.
const LG_FONT: CSSObject = {
  fontSize: "1.75rem",
  lineHeight: "1.875rem"
}

const GAP = { sm: 1, md: 2, lg: 4 }
const INPUT_PADDING = { sm: 2, md: 3, lg: 4 }
const INPUT_WIDTH = { sm: "5rem", md: "6rem", lg: "6.75rem" }

// Matches MUI's InputBase reset, which this value display replaced. Scales with the font.
const TEXT_HEIGHT = "1.4375em"

const BUTTON_PADDING = { sm: 2, md: 3, lg: 4 }
// Mirrors FaIcon's own scale: small = 0.875rem, med = 1rem.
const ICON_FONT = { sm: "0.875rem", md: "1rem", lg: "1rem" }

export const useStyles = makeStyles<StyleParams>()(
  ({ spacing, palette, typography }, { size }) => {
    const inputFont: CSSObject =
      size === "lg"
        ? LG_FONT
        : (typography[size === "md" ? "h3" : "h5"] as CSSObject)

    return {
      container: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing(GAP[size])
      },
      value: {
        ...inputFont,
        // The replaced input inherited its font from theme.typography.body1, not from the DOM.
        fontFamily: typography.fontFamily,
        // content-box keeps padding outside the width, as the input did.
        boxSizing: "content-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: TEXT_HEIGHT,
        // theme spacing is a lookup array, so double the resolved value in CSS, not the index.
        width: `calc(${INPUT_WIDTH[size]} - ${spacing(
          INPUT_PADDING[size]
        )} * 2)`,
        padding: spacing(INPUT_PADDING[size]),
        borderRadius: "2px",
        backgroundColor: alpha(palette.primary[50], 0.7)
      },
      valueDisabled: {
        color: palette.primary[400],
        backgroundColor: alpha(palette.primary[100], 0.7)
      },
      button: {
        // && outranks the shared IconButton's own size classes, which are larger than this control.
        "&&": {
          width: "auto",
          height: "auto",
          padding: spacing(BUTTON_PADDING[size])
        },
        [`&& .${iconClasses.root}`]: {
          fontSize: ICON_FONT[size],
          padding: 0,
          // The shared IconButton makes the icon inherit the button's size; keep it a square.
          width: "1em",
          height: "1em"
        },
        // a11y: bounds use aria-disabled to keep focus, so mirror the native disabled look.
        '&[aria-disabled="true"]': {
          color: palette.primary[200],
          cursor: "default",
          "&:hover": {
            backgroundColor: "transparent",
            color: palette.primary[200]
          }
        }
      }
    }
  }
)
